import React from 'react';

import Button from '../button/Button';

import * as classes from './CreditCardIntro.module.scss';

export default function CreditCardIntro({ dispatch }) {
  return (
    <section className={classes.creditCardIntro}>
      <h3>Key product features</h3>
      <ul>
        <li>Limits of £1,000 to £50,000</li>
        <li>No annual or monthly fees</li>
        <li>No fees for non-sterling transactions</li>
        <li>No late payment fees</li>
        <li>No fees for exceeding your credit limit</li>
        <li>Up to 55 days interest free purchases</li>
        <li>Spending insights and card controls</li>
      </ul>

      <h3>Representative example</h3>
      <div className={classes.representativeExample}>
        <div>
          Representative APR<sup>*</sup>
        </div>
        <div>Purchase rate</div>
        <div>Assumed credit limit</div>
        <div>Annual fee</div>
        <div>19.9% (variable)</div>
        <div>19.9% (variable)</div>
        <div>£1,200</div>
        <div>£0</div>
      </div>
      <p className={classes.legalText}>
        <sup>*</sup>The representative APR is to allow you to compare different credit cards on a like-for-like basis.
        It’s the rate that we will give to at least 51% of our customers. Our purchase rate is how much interest you’ll
        be charged on the card for transactions.
      </p>

      <h3>Eligibility</h3>
      <ul>
        <li>Your business is a limited liability partnership or a limited company.</li>
        <li>Your business must be based - and trade - in the UK.</li>
        <li>You’re an existing Starling business current account customer.</li>
        <li>Your business has been trading for at least two years.</li>
        <li>Your business must be considered viable, which we’ll review as part of our creditworthiness test.</li>
        <li>
          We’ll do credit and fraud checks on your business and its Directors/Person of Significant Control (PSCs).
        </li>
      </ul>

      <h3>What you need to know</h3>
      <ul>
        <li>
          A full credit assessment is completed on all applications. This means that by applying for the credit card,
          this credit assessment will be recorded by our Credit Reference Agency partner and may affect your business’
          credit score.
        </li>
        <li>
          If your credit card application is successful, we’ll need to share information related to your business’
          credit limit, balances and payment performance with our Credit Reference Agency partner for as long as you
          have your credit card.
        </li>
        <li>
          We’ll also require a Personal Guarantee from all directors, where applicable. When you provide a Personal
          Guarantee, you are agreeing to act as a Guarantor for the debt obligations of your business. This means that
          if your business defaults on its credit card payments, you’ve guaranteed that you’ll personally pay instead.
        </li>
        <li>
          Your business is responsible for repayment of the amount used of your credit card limit, plus any interest.
        </li>
        <li>
          There are a number of things you won’t be able to do with the credit card including: withdrawing cash or
          making cash-like transactions (e.g. purchasing foreign currency), transferring money, and making balance
          transfers to the credit card from existing cards.
        </li>
      </ul>

      <h3>What we’ll need to know</h3>
      <p>As part of the application process, we’ll ask for the following information:</p>
      <ul>
        <li>
          Your personal and business details. The person completing the application must be a director or partner of the
          business applying for the facility and be currently authorised to act on its behalf. When we refer to “your
          business or your company” we are referring to a limited liability partnership or a private limited company as
          applicable.
        </li>
        <li>Your business’s registered number with Companies House.</li>
        <li>What your business needs the money for, such as working cash flow.</li>
        <li>The latest filed accounts for your business including your Profit and Loss statement and balance sheet.</li>
        <li>
          Where you hold a non-Starling Bank account for your business, you will need to provide the last six months of
          statements for this account.
        </li>
      </ul>

      <h3>Key documents</h3>
      <p>
        We recommend you read the documents below before applying.
        <br />
        <a href="/docs/credit-card/starling-business-credit-card-summary-box.pdf" target="_blank">
          Summary box
        </a>
        <br />
        <a href="/docs/credit-card/starling-business-credit-card-terms-and-conditions.pdf" target="_blank">
          Terms and Conditions
        </a>
      </p>

      <p className={classes.legalText}>
        Note: If you are successful in your application for a business credit card, we encourage you to seek independent
        advice before accepting the offer. This can help you understand your responsibilities and to make an informed
        decision about whether the credit facility is appropriate for your business’ needs.
      </p>

      <div className={classes.cta}>
        <Button
          variant="secondary"
          onClick={() => {
            dispatch({
              type: 'setStep',
              payload: 1
            });
          }}
        >
          Start your application
        </Button>
      </div>
    </section>
  );
}
